/**
 * Klaviyo fitment tracking, shared by truck-fitment.js and truck-fitment-mini.js.
 *
 * "Truck Search" -- logged-in customers only, sent server-side. The storefront posts
 * just the truck id to the Shopify App Proxy (/apps/peragon/track-search); Shopify
 * forwards it to admin.peragon.com signed with the logged-in customer's id, and the
 * admin looks up the email and truck details itself before sending the event. No
 * email or Klaviyo key ever passes through the browser.
 *
 * "Wishlist" -- when the shopper submits the waitlist Klaviyo form opened from the
 * fitment "Join Our Waitlist" button, the truck they were looking at is recorded on
 * the profile the form just identified. Same event name and properties as the old
 * /shop configurator, so existing flows keep matching. For shoppers who are not
 * logged in, the search that led to the waitlist is recorded as "Truck Search"
 * (Success false) at the same moment -- tied to the email they just typed, without
 * tracking every anonymous search. Browser-side on purpose: the
 * shopper typed the email into Klaviyo's own form, so there is nothing to verify and
 * Klaviyo's onsite script is already loaded (it rendered the form).
 */
(function () {
  'use strict';

  const PROXY_URL = '/apps/peragon/track-search';

  //Waitlist context: the truck a search came up empty for, and the form that would
  //take its signup. Set as soon as the "unavailable" state shows, so the submit is
  //recorded however the form was opened (our button, the auto-open, or Klaviyo's
  //own display rules).
  let pendingWaitlist = null;

  /**
   * @param {String} formId Klaviyo waitlist form for this product line
   * @param {Object} truck  Wishlist event properties (Truck ID, Truck Year, ...); null clears
   */
  function setWaitlist(formId, truck) {
    pendingWaitlist = formId && truck ? { formId: String(formId), truck: truck } : null;
  }

  function trackSearch(truckId, reportingCategoryId) {
    if (!window.peragonCustomerLoggedIn || !truckId) return;

    const body = new URLSearchParams({ truck_id: String(truckId) });
    if (reportingCategoryId) body.set('reporting_category_id', String(reportingCategoryId));

    //keepalive: the mini selector navigates away right after submitting.
    fetch(PROXY_URL, { method: 'POST', body: body, keepalive: true, credentials: 'same-origin' })
      .then(res => {
        if (!res.ok) console.error('[fitment-tracking] track-search failed: HTTP ' + res.status);
      })
      .catch(err => console.error('[fitment-tracking] track-search failed', err));
  }

  /**
   * @param {String} formId Klaviyo form being opened
   * @param {Object} truck  Wishlist event properties (Truck ID, Truck Year, ...)
   */
  function openWaitlist(formId, truck) {
    if (!formId) return;
    setWaitlist(formId, truck);
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  window.addEventListener('klaviyoForms', function (e) {
    const detail = e.detail || {};
    if (detail.type !== 'submit' || !pendingWaitlist || !pendingWaitlist.truck) return;
    if (String(detail.formId) !== pendingWaitlist.formId) return;

    const truck = pendingWaitlist.truck;
    pendingWaitlist = null;

    //The search that led here, recorded only now that there is an email to tie it to.
    //Logged-in customers already got it server-side when they searched.
    const search = window.peragonCustomerLoggedIn
      ? null
      : Object.assign({}, truck, { 'Success': false });

    const email = detail.metaData && detail.metaData.$email;
    const klaviyo = window.klaviyo;

    if (klaviyo && typeof klaviyo.track === 'function') {
      //Identify first so the events land on the profile that just signed up, not
      //whatever anonymous or previous profile the browser was carrying.
      const identified = email && typeof klaviyo.identify === 'function'
        ? Promise.resolve(klaviyo.identify({ email: email }))
        : Promise.resolve();
      identified
        .then(() => search ? klaviyo.track('Truck Search', search) : null)
        .then(() => klaviyo.track('Wishlist', truck))
        .catch(err => console.error('[fitment-tracking] Wishlist track failed', err));
      return;
    }

    //Older onsite queue -- what the /shop configurator used.
    window._klOnsite = window._klOnsite || [];
    if (search) window._klOnsite.push(['track', 'Truck Search', search]);
    window._klOnsite.push(['track', 'Wishlist', truck]);
  });

  window.PeragonFitmentTracking = {
    trackSearch: trackSearch,
    setWaitlist: setWaitlist,
    openWaitlist: openWaitlist
  };
})();
